import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Popup from '../../../../Utilities/components/Popup';
import CardDisplay from '../../../../Utilities/components/CardDisplay/CardDisplay';
import { StateContext } from '../../../../StateProvider';
import { processStripePayment, updateWalletBalance } from '../../../../Utilities/api';

function formatPaymentFailure(res) {
  if (!res) return 'Payment failed. Please try again.';

  const msg =
    (res.error && (res.error.message || res.error)) ||
    res.message ||
    null;

  const code =
    (res.error && res.error.code) ||
    res.code ||
    null;

  const decline =
    (res.error && res.error.decline_code) ||
    res.decline_code ||
    null;

  if (code === 'card_declined' || decline) {
    if (decline === 'insufficient_funds') return 'Card was declined for insufficient funds. Try a different card or contact your bank.';
    if (decline === 'do_not_honor') return 'The bank declined this card. Try another card or contact your bank.';
    if (decline === 'lost_card') return 'This card has been reported lost. Please use a different card.';
    if (decline === 'stolen_card') return 'This card has been reported stolen. Please use a different card.';
    if (decline === 'fraudulent') return 'The bank flagged this as suspicious. Try another card or call your bank.';
    return 'The bank declined this card. Please try another card.';
  }

  if (code === 'authentication_required' || code === 'requires_action') {
    return 'Your bank needs a quick verification for this card. Use a different card or contact your bank to enable online payments.';
  }

  if (code === 'processing_error') {
    return 'There was a processing error. Please try again in a moment.';
  }

  if (msg) return String(msg);
  return 'Payment failed. Please try again.';
}

function formatException(err) {
  if (err && err.code === 'ERR_NETWORK') {
    return 'We can’t reach the payment service. Check your internet and try again.';
  }

  if (err && err.response) {
    const { status, data } = err.response;
    const nested =
      (data && data.message) ||
      (data && data.error && (data.error.message || data.error)) ||
      (Array.isArray(data && data.errors) && data.errors.length > 0 && (data.errors[0].message || data.errors[0])) ||
      null;

    if (status === 402) return formatPaymentFailure(data || { code: 'card_declined' });
    if (status === 429) return 'The payment service is busy. Please try again shortly.';
    if (status >= 500) return 'The payment service had an issue. Please try again in a moment.';
    if (nested) return String(nested);
  }

  if (err && (err.code || err.decline_code || err.type)) {
    return formatPaymentFailure(err);
  }

  if (err && err.message) return String(err.message);
  return 'There was an error processing your payment. Please try again.';
}

const toCents = (val) => {
  const n = Number(val);
  if (Number.isNaN(n) || n <= 0) return 0;
  return Math.round(n * 100);
};

const LiveChargeTest = () => {
  const navigate = useNavigate();
  const { user, paymentMethods, setLoading, refreshUserData } = useContext(StateContext);

  const [amount, setAmount] = useState('1.00'); // dollars
  const [description, setDescription] = useState('Live charge test via Pawsible Console');
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupKind, setPopupKind] = useState(''); // 'warn', 'success', 'error'
  const [popupText, setPopupText] = useState('');
  const [resultId, setResultId] = useState(null);

  const isLive = useMemo(() => {
    const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    return pk.startsWith('pk_live_');
  }, []);

  const defaultMethod = useMemo(() => {
    if (!paymentMethods || !Array.isArray(paymentMethods) || paymentMethods.length === 0) return null;
    if (user && user.defaultPaymentMethodId) {
      const m = paymentMethods.find(m => String(m.id) === String(user.defaultPaymentMethodId));
      return m || paymentMethods[0];
    }
    return paymentMethods[0];
  }, [paymentMethods, user]);

  const openPopup = (kind, text, id = null) => {
    setPopupKind(kind);
    setPopupText(text);
    setResultId(id);
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setPopupKind('');
    setPopupText('');
    setResultId(null);
  };

  const handleCharge = async (e) => {
    e.preventDefault();

    // Safety checks before spending a dollar
    if (!isLive) return openPopup('warn', 'You are not using LIVE Stripe keys. Switch to live mode and try again.');
    if (!user || !user.uid || !user.stripeCustomerId) return openPopup('warn', 'No live customer profile found. Add a card in live mode, then try again.');
    if (!defaultMethod || !defaultMethod.stripePaymentMethodId) return openPopup('warn', 'No saved payment method. Add a card first.');
    const cents = toCents(amount);
    if (cents < 50) return openPopup('warn', 'Amount must be at least $0.50 in live mode.');

    setLoading(true);
    try {
      // cart: keep it minimal for the audit trail (Stripe metadata on your server can add detail)
      const minimalCart = [{ bookingType: 'LiveTest', bookingInfo: {}, originalPrice: cents / 100, discountedPrice: cents / 100 }];

      const response = await processStripePayment(
        user.stripeCustomerId,
        defaultMethod.stripePaymentMethodId,
        minimalCart,
        cents / 100, // your API expects dollars (matches Checkout usage)
        'usd',
        description || 'Live charge test',
        'automatic',
        user
      );

      if (response && response.status === 'succeeded') {
        // No wallet updates for a test charge—this is a clean payment-only probe.
        await refreshUserData(user.uid);
        openPopup('success', 'Live payment succeeded. You can refund this charge in your Stripe Dashboard.', response.id || response.payment_intent || null);
      } else {
        openPopup('error', formatPaymentFailure(response));
      }
    } catch (err) {
      openPopup('error', formatException(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 520, padding: 16 }}>
      <h2 className="account-title">Live Charge Test</h2>
      <p className="profile-popup-message" style={{ marginTop: 6 }}>
        This will charge a real card. Use a small amount like $1.00, then refund in Stripe.
      </p>

      <div className="info-card" style={{ padding: 10, margin: '12px 0' }}>
        <div className="info-card-title">Stripe Mode</div>
        <div className="info-location-card-info">{isLive ? 'LIVE' : 'TEST'}</div>
      </div>

      <div className="checkout-payment-container" style={{ marginTop: 10 }}>
        <h3 className="checkout-card-title">Payment Method</h3>
        {defaultMethod ? (
          <div onClick={() => navigate(`/profile/wallet/payment-methods/edit-payment/${defaultMethod.id}`)}>
            <CardDisplay key={defaultMethod.id} method={defaultMethod} />
          </div>
        ) : (
          <div className="profile-popup-message">No saved card. Add one in Wallet.</div>
        )}
      </div>

      <form onSubmit={handleCharge} className="checkout-form-container" style={{ marginTop: 16 }}>
        <section className="checkout-policies-container">
          <div className="checkout-terms-and-conditions">
            <label className="checkout-policies-label" style={{ display: 'block', marginBottom: 10 }}>
              Amount (USD)
              <input
                type="number"
                step="0.01"
                min="0.50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 6 }}
              />
            </label>

            <label className="checkout-policies-label" style={{ display: 'block' }}>
              Description
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 6 }}
              />
            </label>
          </div>
        </section>

        <div className="checkout-confirmation-container" style={{ marginTop: 14 }}>
          <button type="submit" disabled={!isLive || !defaultMethod}>
            Charge Now
          </button>
        </div>
      </form>

      <Popup isOpen={popupOpen} handleClose={closePopup}>
        <div className="profile-popup">
          <h2 className="profile-popup-header">
            {popupKind === 'success' ? 'Payment Succeeded' : popupKind === 'warn' ? 'Heads Up' : 'Payment Issue'}
          </h2>
          <p className="profile-popup-message">{popupText}</p>

          {popupKind === 'success' && (
            <p className="profile-popup-message" style={{ marginTop: 8 }}>
              Tip: Refund this test in your Stripe Dashboard → Payments. {resultId ? `Ref: ${resultId}` : ''}
            </p>
          )}

          <div className="profile-popup-actions-confirmation">
            <button onClick={closePopup} className="profile-popup-update-button-ok">OK</button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default LiveChargeTest;
